import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withMetadata,
    withSearchFilter,
    withSearchFilterArrayUnTyped,
    withSearchFilterUnTyped,
} from '@pf/shared-utility';
import {
    SlotCreate,
    SlotDto,
    SlotRead,
    SlotSearchParams,
} from '../../entities/slot.dto';
import {
    CustomFieldMetadataMap,
    DefaultLocationFragmentMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { ZoneMetadataMap } from '../zones/zone.mapping-profile';

const symbols = entitySymbols(PFWmsEntities.Slot);

withDefaultEntityMetadata<SlotDto>(symbols.dto, {
    slotStatus: String,
    slotPriority: Number,
    zoneId: String,
    zone: ZoneMetadataMap.symbols.dto,
    barcode: String,
    resourceAuth: ResourceAuthSymbol,
    customFields: CustomFieldMetadataMap.symbols.dto,
    slotType: String,
    minQuantityOverride: Number,
    maxQuantityOverride: Number,
    replenishFromSlotId: String,
    containers: [Object],
    reservedForLocation: DefaultLocationFragmentMetadataMap.symbols.dto,
    reservedForItem: Object,
    reservedForItemCategory: Object,
    excludeFromOperations: Boolean,
    equipmentType: Object,
});

withDefaultEntityMetadata<SlotRead>(symbols.entity, {
    slotStatus: String,
    slotPriority: Number,
    barcode: String,
    resourceAuth: ResourceAuthSymbol,
    customFields: CustomFieldMetadataMap.symbols.entity,
    slotType: String,
    minQuantityOverride: Number,
    maxQuantityOverride: Number,
    replenishFromSlotId: String,
    zone: ZoneMetadataMap.symbols.entity,
    reservedForLocation: DefaultLocationFragmentMetadataMap.symbols.entity,
    reservedForItem: Object,
    reservedForItemCategory: Object,
    excludeFromOperations: Boolean,
    containers: [Object],
    equipmentType: Object,
});

withMetadata<SlotCreate>(symbols.createBody, {
    slotStatus: String,
    slotPriority: Number,
    reservedForLocationId: String,
    zoneId: String,
    barcode: String,
    resourceAuth: ResourceAuthSymbol,
    slotType: String,
    minQuantityOverride: Number,
    maxQuantityOverride: Number,
    replenishFromSlotId: String,
    excludeFromOperations: Boolean,
    equipmentTypeId: String,
    reservedForItemId: String,
    reservedForItemCategoryId: String,
});

withDefaultSearchParamsMetadata<SlotSearchParams>(symbols.searchParams, {
    reservedForLocationId: String,
    zoneId: String,
    barcode: String,
    itemId: String,
    hasContainers: Boolean,
    slotType: String,
    warehouseId: String,
    zoneLocationIdentifier: String,
    equipmentTypeIds: [String],
    equipmentCategories: [String],
});

export const SlotMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<SlotRead, SlotDto>(
            mapper,
            symbols.entity,
            symbols.dto,
            forMember(
                d => d.reservedForItemCategory,
                mapFrom(s => s.reservedForItemCategory)
            )
        );
        createMap<SlotDto, SlotRead>(mapper, symbols.dto, symbols.entity);
        createMap<SlotDto, SlotCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                d => d.zoneId,
                mapFrom(s => s.zoneId || s.zone?.id || null)
            ),
            forMember(
                d => d.equipmentTypeId,
                mapFrom(s => s.equipmentType?.id || null)
            ),
            forMember(
                d => d.reservedForLocationId,
                mapFrom(s => s.reservedForLocation?.id || null)
            ),
            forMember(
                d => d.reservedForItemId,
                mapFrom(s => s.reservedForItem?.id || null)
            ),
            forMember(
                d => d.reservedForItemCategoryId,
                mapFrom(s => s.reservedForItemCategory?.id || null)
            )
        );
        createMap<IQueryParams<SlotDto>, SlotSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('barcode'),
            withSearchFilter('reservedForLocation'),
            withSearchFilter('zoneId'),
            withSearchFilter('slotType'),
            withSearchFilterUnTyped('hasContainers'),
            withSearchFilterUnTyped('itemId'),
            withSearchFilterUnTyped('warehouseId'),
            withSearchFilterUnTyped('zoneLocationIdentifier'),
            withSearchFilterArrayUnTyped('equipmentTypeIds'),
            withSearchFilterArrayUnTyped('equipmentCategories'),
            ...withDefaultSearchParamsMappingConfiguration()
        );

        ZoneMetadataMap.profile(mapper);
        DefaultLocationFragmentMetadataMap.profile(mapper);
        CustomFieldMetadataMap.profile(mapper);
    },
};
